import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  Button,
  Divider
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import BlogPostPreview from '../components/BlogPostPreview';
import { Helmet } from 'react-helmet';

/**
 * @component BlogPage
 * @description Blog listing page for Laprecio ethical mineral trading insights
 *
 * SEO Considerations:
 * - Implements proper meta tags with Helmet
 * - Uses semantic HTML structure with proper heading hierarchy
 * - Includes keyword-rich content about ethical mineral trading
 * - Provides filtering by categories for better user experience
 * - Structured with proper heading levels (h1, h2, h3)
 */
const BlogPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Blog post data with SEO-optimized content
  const blogPosts = [
    {
      id: 1,
      title: 'How to Buy Ethical Minerals Directly from Global Miners: A Complete Guide',
      slug: 'ethical-minerals-guide',
      excerpt: 'Learn how to source ethical minerals directly from global miners. This comprehensive guide covers verification, logistics, payment security, and regulatory compliance.',
      date: 'May 15, 2023',
      image: '/images/blog/ethical-minerals-trading.jpg',
      imageAlt: 'Ethical mineral trading process showing direct sourcing from global miners',
      categories: ['ethical-sourcing', 'supply-chain'],
      tags: ['Ethical Sourcing', 'Global Minerals', 'Supply Chain', 'Blockchain Verification']
    },
    {
      id: 2,
      title: 'Blockchain Technology in Mineral Supply Chains: Beyond the Hype',
      slug: 'blockchain-mineral-supply-chains',
      excerpt: 'Explore how blockchain technology is transforming mineral supply chains with immutable verification, real-time tracking, and simplified compliance.',
      date: 'June 10, 2023',
      image: '/images/blog/blockchain-verification.jpg',
      imageAlt: 'Blockchain verification process for ethical mineral supply chains',
      categories: ['blockchain', 'technology'],
      tags: ['Blockchain', 'Supply Chain', 'Technology', 'Verification', 'Digital Trust']
    },
    {
      id: 3,
      title: 'Navigating Global Mineral Regulations: A Comprehensive Guide',
      slug: 'global-mineral-regulations',
      excerpt: 'Stay compliant with international regulations including EU Conflict Minerals Regulation, US Dodd-Frank Act, and OECD Due Diligence Guidance.',
      date: 'July 5, 2023',
      image: '/images/blog/regulatory-compliance.jpg',
      imageAlt: 'Regulatory compliance documentation for ethical mineral trading',
      categories: ['compliance', 'regulatory'],
      tags: ['Compliance', 'Regulations', 'Due Diligence', 'EU Regulations', 'Global Trade']
    }
  ];

  // Blog categories with SEO-optimized labels
  const categories = [
    { id: 'all', label: 'All Articles' },
    { id: 'ethical-sourcing', label: 'Ethical Sourcing' },
    { id: 'blockchain', label: 'Blockchain Verification' },
    { id: 'supply-chain', label: 'Supply Chain' },
    { id: 'compliance', label: 'Regulatory Compliance' },
    { id: 'technology', label: 'Technology' }
  ];

  /**
   * Filters blog posts based on active category and search query
   *
   * @returns {Array} Filtered blog posts
   */
  const getFilteredPosts = () => {
    return blogPosts.filter(post => {
      // Filter by category
      const categoryMatch = activeCategory === 'all' || post.categories.includes(activeCategory);

      // Filter by search query
      const searchMatch = searchQuery === '' ||
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));

      return categoryMatch && searchMatch;
    });
  };

  const filteredPosts = getFilteredPosts();

  return (
    <>
      <Helmet>
        <title>Laprecio Blog | Ethical Mineral Trading Insights and Resources</title>
        <meta name="description" content="Explore insights, guides, and resources about ethical mineral trading, global sourcing, blockchain verification, and sustainable supply chains." />
        <meta name="keywords" content="ethical minerals blog, mineral trading resources, ethical sourcing guides, global miners, mineral supply chain, blockchain verification, digital trust, ethical cobalt, ethical copper, ethical lithium, ethical gold" />
        <link rel="canonical" href="https://laprecio.com/blog" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://laprecio.com/blog" />
        <meta property="og:title" content="Laprecio Blog | Ethical Mineral Trading Insights and Resources" />
        <meta property="og:description" content="Explore insights, guides, and resources about ethical mineral trading, global sourcing, blockchain verification, and sustainable supply chains." />
        <meta property="og:image" content="https://laprecio.com/images/laprecio-logo.png" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://laprecio.com/blog" />
        <meta property="twitter:title" content="Laprecio Blog | Ethical Mineral Trading Insights and Resources" />
        <meta property="twitter:description" content="Explore insights, guides, and resources about ethical mineral trading, global sourcing, blockchain verification, and sustainable supply chains." />
        <meta property="twitter:image" content="https://laprecio.com/images/laprecio-logo.png" />
      </Helmet>

      <Box
        sx={{
          background: 'linear-gradient(to right, #fffef0, #fffde7)',
          py: 8,
          mb: 6
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            align="center"
            gutterBottom
            sx={{
              fontWeight: 'bold',
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}
          >
            Ethical Mineral Trading Insights
          </Typography>

          <Typography
            variant="h2"
            align="center"
            color="text.secondary"
            sx={{
              maxWidth: 800,
              mx: 'auto',
              mb: 6,
              fontSize: { xs: '1.1rem', md: '1.3rem' }
            }}
          >
            Explore our latest articles, guides, and resources about ethical mineral sourcing, blockchain verification, and sustainable supply chains.
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <TextField
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              variant="outlined"
              sx={{
                width: { xs: '100%', sm: '400px' },
                bgcolor: 'white',
                borderRadius: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}>
            {categories.map(category => (
              <Chip
                key={category.id}
                label={category.label}
                onClick={() => setActiveCategory(category.id)}
                sx={{
                  px: 1,
                  borderRadius: 50,
                  bgcolor: activeCategory === category.id ? '#eab308' : 'rgba(234, 179, 8, 0.1)',
                  color: activeCategory === category.id ? 'white' : '#ca8a04',
                  fontWeight: activeCategory === category.id ? 'bold' : 'normal',
                  '&:hover': {
                    bgcolor: activeCategory === category.id ? '#ca8a04' : 'rgba(234, 179, 8, 0.2)'
                  }
                }}
              />
            ))}
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mb: 8 }}>
        {filteredPosts.length > 0 ? (
          <Grid container spacing={4}>
            {filteredPosts.map(post => (
              <Grid item xs={12} sm={6} md={4} key={post.id}>
                <BlogPostPreview post={post} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h5" gutterBottom>
              No articles found
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Try adjusting your search or category filters
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 8 }} />

        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
            Subscribe to Our Newsletter
          </Typography>

          <Typography variant="body1" sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
            Stay updated with the latest insights, guides, and news about ethical mineral trading and supply chain innovation.
          </Typography>

          <Box sx={{ display: 'flex', maxWidth: 500, mx: 'auto', flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              placeholder="Your email address"
              variant="outlined"
              fullWidth
              sx={{
                mr: { xs: 0, sm: 1 },
                mb: { xs: 2, sm: 0 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: { xs: 2, sm: '8px 0 0 8px' }
                }
              }}
            />
            <Button
              variant="contained"
              color="primary"
              sx={{
                px: 3,
                borderRadius: { xs: 2, sm: '0 8px 8px 0' },
                height: { xs: 56, sm: 'auto' }
              }}
            >
              Subscribe
            </Button>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default BlogPage;
